import { useCallback, useEffect, useRef, useState } from 'react';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';

/**
 * Custom hook for real-time vehicle report updates via Supabase Realtime.
 *
 * Fetches initial vehicle_reports data via RLS query and subscribes to
 * real-time updates for a specific report. Automatically updates state
 * when report status or other fields change.
 *
 * @param {string} reportId - The ID of the vehicle report to monitor
 * @param {Object} session - The Supabase session object for authentication
 * @returns {Object} - { report, isLoading, error }
 */
export const useReportRealtime = (reportId, session) => {
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);

  // Helper to detect if an error is an auth/JWT error
  const isAuthError = useCallback((err) => {
    if (!err) return false;
    const errorMessage = err.message || err.toString();
    return (
      errorMessage.includes('JWT') ||
      errorMessage.includes('Token has expired') ||
      errorMessage.includes('Invalid token') ||
      err.code === 'PGRST301' || // PostgREST JWT error
      err.code === '401'
    );
  }, []);

  // Helper to check if error is auth-related and set error state
  const handleFetchError = useCallback(
    (fetchError) => {
      if (isAuthError(fetchError)) {
        const authError = new Error(fetchError.message || 'Session expired. Please log in again.');
        authError.isAuthError = true;
        authError.originalError = fetchError;
        setError(authError);
      } else {
        setError(fetchError);
      }
    },
    [isAuthError]
  );

  // Main effect for subscribing to real-time updates
  useEffect(() => {
    if (!session?.user || !reportId) {
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    // Fetch initial data and set up realtime subscription serially. Awaiting the
    // initial fetch BEFORE subscribing closes the SP-013 paired race: a realtime
    // UPDATE that fires before the REST round-trip finishes used to clobber
    // `page_images` and `vehicle_models` to null because the merge function read
    // them from `prevReport`, which was still null. Now `prevReport` is guaranteed
    // populated (or in error state) before the first realtime payload can arrive.
    const loadInitialData = async () => {
      logger.log('[useReportRealtime] Fetching initial report data:', reportId);

      // SP-013: project only the JSONB key the viewer actually consumes (page_images).
      // The rest of `content` is the LLM-generated report body — the paid deliverable —
      // and must not flow back to the browser via REST. Pairs with M6 (Realtime
      // publication strips `content`) and the realtime handler below preserves
      // page_images across status updates.
      const { data, error: fetchError } = await supabase
        .from('vehicle_reports')
        .select(`
          id,
          user_id,
          model_id,
          status,
          pdf_url,
          job_id,
          pdf_job_id,
          created_at,
          updated_at,
          downloaded_at,
          page_images:content->page_images,
          vehicle_models (
            model_id,
            make,
            model,
            year_range,
            trim
          )
        `)
        .eq('id', reportId)
        .single();

      // Only update state if component is still mounted
      if (isMounted) {
        if (fetchError) {
          handleFetchError(fetchError);
        } else if (data) {
          setReport(data);
        }
        setIsLoading(false);
      }
    };

    const handleReportUpdate = (payload) => {
      logger.log('[useReportRealtime] Received update:', payload.eventType);

      const updatedData = payload.new;
      const eventType = payload.eventType;

      if (eventType === 'UPDATE' || eventType === 'INSERT') {
        // Realtime payloads carry only the columns in the supabase_realtime publication
        // (post-M6 the column list excludes `content` and the joined vehicle_models). Merge
        // the new column values onto prevReport so the initial-fetch JSONB projection
        // (page_images) and the join survive every status update.
        setReport((prevReport) => ({
          ...updatedData,
          page_images: prevReport?.page_images ?? null,
          vehicle_models: prevReport?.vehicle_models || null,
        }));
        logger.log('[useReportRealtime] Report updated:', {
          id: updatedData.id,
          status: updatedData.status,
        });
      } else if (eventType === 'DELETE') {
        // Handle report deletion
        logger.log('[useReportRealtime] Report deleted:', reportId);
        setReport(null);
      }
    };

    // Run the load + subscribe sequence as an async IIFE so we can `await` the
    // initial load before opening the channel. If the component unmounts during
    // the load (isMounted flips false), we return early and never subscribe.
    (async () => {
      await loadInitialData();
      if (!isMounted) return;

      const channel = supabase.channel(`report:${reportId}`);
      channelRef.current = channel;

      channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'vehicle_reports',
            filter: `id=eq.${reportId}`,
          },
          handleReportUpdate
        )
        .subscribe((status, err) => {
          if (err) {
            logger.error('[useReportRealtime] Subscription error:', err);
          }

          if (status === 'SUBSCRIBED') {
            logger.log('[useReportRealtime] Subscribed to report updates:', reportId);
          }
        });
    })();

    // Cleanup subscription on unmount or when dependencies change
    return () => {
      isMounted = false;
      if (channelRef.current) {
        logger.log('[useReportRealtime] Unsubscribing from report updates:', reportId);
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [reportId, session?.user, handleFetchError]);

  return {
    report,
    isLoading,
    error,
  };
};
